import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

class ProductBadgesBar extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;

    this.closest('.shopify-section, dialog')?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );

    this.addEventListener('click', this.#onClick, { signal });
    this.#bindDialog();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  #onVariantUpdate = (event) => {
    const { data } = event.detail;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const next = data.html.querySelector(`product-badges-bar[data-block-id="${this.dataset.blockId}"]`);
    if (next) {
      morph(this, next, { childrenOnly: true });
      this.#bindDialog();
    }
  };

  #bindDialog = () => {
    const dialog = this.#shareDialog;
    if (!(dialog instanceof HTMLDialogElement) || dialog.dataset.shareBound === 'true') return;

    dialog.dataset.shareBound = 'true';
    dialog.addEventListener('click', this.#onDialogClick);
    dialog.addEventListener('close', this.#onDialogClose);
  };

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-share-close]')) {
      event.preventDefault();
      this.#closeShareDialog();
      return;
    }

    const shareButton = event.target.closest('[data-share]');
    if (shareButton) {
      event.preventDefault();
      event.stopPropagation();
      this.#handleShare();
      return;
    }

    const copyButton = event.target.closest('[data-copy-link]');
    if (copyButton instanceof HTMLButtonElement) {
      event.preventDefault();
      void this.#handleCopy(copyButton);
    }
  };

  #onDialogClick = (event) => {
    if (event.target === this.#shareDialog) {
      this.#closeShareDialog();
    }
  };

  #onDialogClose = () => {
    this.#shareButton?.removeAttribute('data-share-open');
  };

  get #shareDialog() {
    return this.querySelector('[data-share-dialog]');
  }

  get #shareButton() {
    return this.querySelector('[data-share]');
  }

  /**
   * @returns {boolean}
   */
  #canNativeShare() {
    if (!navigator.share) return false;

    const data = { url: window.location.href };

    try {
      return typeof navigator.canShare !== 'function' || navigator.canShare(data);
    } catch {
      return false;
    }
  }

  #handleShare() {
    if (this.#canNativeShare()) {
      navigator
        .share({ url: window.location.href, title: document.title })
        .then(() => this.#closeShareDialog())
        .catch((err) => {
          if (err instanceof Error && err.name === 'AbortError') return;
          this.#openShareDialog();
        });
      return;
    }

    this.#openShareDialog();
  }

  #openShareDialog() {
    const dialog = this.#shareDialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#updateShareDialogLinks();

    if (!dialog.open) {
      dialog.showModal();
    }

    this.#shareButton?.setAttribute('data-share-open', '');
  }

  #closeShareDialog() {
    const dialog = this.#shareDialog;
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      dialog.close();
    }
  }

  #updateShareDialogLinks() {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(document.title);

    this.querySelector('[data-share-whatsapp]')?.setAttribute('href', `https://wa.me/?text=${url}`);
    this.querySelector('[data-share-facebook]')?.setAttribute(
      'href',
      `https://www.facebook.com/sharer/sharer.php?u=${url}`
    );
    this.querySelector('[data-share-twitter]')?.setAttribute(
      'href',
      `https://twitter.com/intent/tweet?url=${url}&text=${title}`
    );
    this.querySelector('[data-share-email]')?.setAttribute(
      'href',
      `mailto:?subject=${title}&body=${url}`
    );
  }

  /**
   * @param {HTMLButtonElement} button
   * @returns {Promise<void>}
   */
  #handleCopy = async (button) => {
    const url = window.location.href;
    const copiedLabel = button.dataset.copiedLabel || 'Copied';
    const label = button.querySelector('[data-copy-label]');

    try {
      await this.#copyText(url);

      if (label instanceof HTMLElement) {
        const originalLabel = label.textContent;
        label.textContent = copiedLabel;
        window.setTimeout(() => {
          label.textContent = originalLabel;
        }, 2000);
      }

      this.#closeShareDialog();
    } catch {
      /* clipboard blocked */
    }
  };

  /**
   * @param {string} text
   * @returns {Promise<void>}
   */
  #copyText = async (text) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch {
        /* fallback below */
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };
}

if (!customElements.get('product-badges-bar')) {
  customElements.define('product-badges-bar', ProductBadgesBar);
}
